import React, { createContext, useContext, useEffect, useMemo } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { useRouter } from 'next/router'

import { useOrganizations } from '@/hooks/organization/useOrganizations'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import {
  type OrgsOverviewSelectionMode,
  useOrgsOverviewSelection,
  useOrgsOverviewSelectionStore,
  useSyncOrgsOverviewScopeToUser,
} from '@/stores/OrgsOverviewSelectionStore'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Organization } from '@/types/Organization'

interface OrgsOverviewContextValue {
  /** Orgs the current user can see. */
  allOrgs: Organization[]
  /** Org IDs to actually fetch for the current organization scope. */
  effectiveOrgIds: UUID[]
  /** Map for cheap lookup of org metadata by id (name, etc). */
  orgsById: Record<UUID, Organization>
  /** True iff the user is using the implicit all-organizations scope. */
  isAllSelected: boolean
  selectionMode: OrgsOverviewSelectionMode
}

interface OrgsOverviewOrganizationData {
  allOrgs: Organization[]
  orgsById: Record<UUID, Organization>
}

export const OrgsOverviewContext = createContext<
  OrgsOverviewContextValue | undefined
>(undefined)

interface ProviderProps {
  children: React.ReactNode
}

type OrganizationsQueryData = InfiniteData<
  PaginatedResponse<Organization>,
  PaginateRequest
>

const EMPTY_ORGS_OVERVIEW_ORGANIZATION_DATA: OrgsOverviewOrganizationData = {
  allOrgs: [],
  orgsById: {},
}

export const resolveEffectiveOrgIds = (
  selectedOrgIds: UUID[],
  allOrgs: Organization[] | undefined,
  selectionMode: OrgsOverviewSelectionMode,
): UUID[] => {
  if (!allOrgs || allOrgs.length === 0) return []
  if (selectionMode === 'all') return allOrgs.map((o) => o.id as UUID)
  const validIds = new Set(allOrgs.map((o) => o.id))
  return selectedOrgIds.filter((id) => validIds.has(id))
}

const selectOverviewOrganizationData = (
  data: OrganizationsQueryData,
): OrgsOverviewOrganizationData => {
  // Sort once here so every downstream consumer of `overview.allOrgs`
  // (FilterPopover, OrgsMultiSelect, OrgComparison, etc.) can render
  // alphabetically without re-sorting locally.
  const orgs = data.pages
    .flatMap((page) => page.data)
    .toSorted((a, b) => a.name.localeCompare(b.name))
  const orgsById: Record<UUID, Organization> = {}
  for (const org of orgs) {
    orgsById[org.id as UUID] = org
  }

  return {
    allOrgs: orgs,
    orgsById,
  }
}

export const OrgsOverviewProvider: React.FC<ProviderProps> = ({ children }) => {
  const { user } = useAuthInfoWithThrow()
  const userId = user.userId
  const syncScopeToUser = useSyncOrgsOverviewScopeToUser()

  // Clears persisted selection from a previous user sharing this browser.
  useEffect(() => {
    if (userId) syncScopeToUser(userId)
  }, [userId, syncScopeToUser])

  const { selectionMode, selectedOrgIds } = useOrgsOverviewSelection()

  const { data: organizationData = EMPTY_ORGS_OVERVIEW_ORGANIZATION_DATA } =
    useOrganizations({
      select: selectOverviewOrganizationData,
    })

  const effectiveOrgIds = useMemo(
    () =>
      resolveEffectiveOrgIds(
        selectedOrgIds,
        organizationData.allOrgs,
        selectionMode,
      ),
    [organizationData.allOrgs, selectedOrgIds, selectionMode],
  )

  const value = useMemo<OrgsOverviewContextValue>(
    () => ({
      allOrgs: organizationData.allOrgs,
      effectiveOrgIds,
      orgsById: organizationData.orgsById,
      isAllSelected: selectionMode === 'all',
      selectionMode,
    }),
    [
      effectiveOrgIds,
      organizationData.allOrgs,
      organizationData.orgsById,
      selectionMode,
    ],
  )

  return (
    <OrgsOverviewContext.Provider value={value}>
      {children}
    </OrgsOverviewContext.Provider>
  )
}

export const OrgsOverviewRouteSelectionSync: React.FC = () => {
  const router = useRouter()
  const { data: allOrgs } = useOrganizations()

  const { isReady, pathname, query, replace } = router
  const rawAddOrgId = query.addOrg
  const addOrgId = Array.isArray(rawAddOrgId) ? rawAddOrgId[0] : rawAddOrgId

  useEffect(() => {
    if (!isReady || !addOrgId || !allOrgs) return

    const validOrgIds = new Set(allOrgs.map((org) => org.id))
    if (validOrgIds.has(addOrgId as UUID)) {
      // Read fresh state here so this one-shot URL command does not subscribe
      // the overview provider to selection mutations.
      const state = useOrgsOverviewSelectionStore.getState()
      if (
        state.selectionMode === 'custom' &&
        !state.selectedOrgIds.includes(addOrgId as UUID)
      ) {
        state.setSelectedOrgIds([...state.selectedOrgIds, addOrgId as UUID])
      }
    }

    const { addOrg, ...rest } = query
    void replace({ pathname, query: rest }, undefined, { shallow: true })
  }, [addOrgId, allOrgs, isReady, pathname, query, replace])

  return null
}

export const useOptionalOrgsOverview = ():
  | OrgsOverviewContextValue
  | undefined => useContext(OrgsOverviewContext)

export const useOrgsOverview = (): OrgsOverviewContextValue => {
  const ctx = useOptionalOrgsOverview()
  if (!ctx) {
    throw new Error('useOrgsOverview must be used inside OrgsOverviewProvider')
  }
  return ctx
}
